import { type ChangeEvent, useState, useEffect, Fragment } from "react";
import { useTranslation } from "react-i18next";

import {
  Checkbox,
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownMenuItem,
  Input,
  PopperTooltip,
  ToggleBox
} from "@unified-checkout/design-system";
import { CircleInfo } from "@unified-checkout/design-system/icons/index";

import { useLangStore } from "@app/dashboard/store/lang";
import { useCheckoutCustomizationStore } from "@app/dashboard/store/checkout-customization";
import { required, urlValidation } from "@app/dashboard/utils/formValidations";
import { CUSTOMIZATION_SECTIONS_KEYS } from "../../../../utils";
import { Title } from "../../../../components/Title";
import styles from "./additional-info.module.scss";

type TOption = { label: string, hint?: string, value: string, flag?: string };
let MAX_ALLOWED_CARDS_OPTIONS = [2, 3];
const { TERMS_URL } = CUSTOMIZATION_SECTIONS_KEYS;

const SHOW_TERMS = 'showTerms';
const SPLIT_PAYMENT_ENABLED = 'enabled';

const TOGGLES_KEYS = [
  'allow_payment_cancellation',
  'allow_payments_retries',
];

const CHECKBOXES_KEYS = [
  'display_billing',
  'display_shipping',
  'display_items',
  'display_savecard',
  'enable_instant_refund',
];

export const AdditionalInfo = ({ merchantFlags }: { merchantFlags: string[] }) => {
  const { t } = useTranslation();
  const { lang } = useLangStore();

  const {
    isFetched,
    isDataReset,
    customizationData,
    setCustomizationData,
    setErrors
  } = useCheckoutCustomizationStore();

  const { payments } = customizationData;
  const { toggles, showTerms, termsUrl, split_payment } = payments;
  const { enabled: split_payment_enabled, max_allowed_cards } = split_payment || {};

  const additionalCheckboxesOptions: TOption[] = t('innerLayout.checkoutCustomizationPage.tabs.payment.sections.additionalInfo.checkboxOptions', { returnObjects: true });
  const splitPaymentOptions: TOption[] = t('innerLayout.checkoutCustomizationPage.tabs.payment.sections.splitPayment.toggleOptions', { returnObjects: true });
  const additionalToggleOptions: TOption[] = t('innerLayout.checkoutCustomizationPage.tabs.payment.sections.paymentExperience.toggleOptions', { returnObjects: true });

  const [isInitialized, setIsInitialized] = useState(false);
  const [checkedOptions, setCheckedOptions] = useState<TOption[]>([]);
  const [selectedToggles, setSelectedToggles] = useState<TOption[]>([]);
  const [maxCardsSelected, setMaxCardsSelected] = useState(MAX_ALLOWED_CARDS_OPTIONS[0]);

  const [isInputVisible, setIsInputVisible] = useState(false);
  const [inputValue, setInputValue] = useState({
    value: '',
    isValid: undefined as boolean | undefined,
    errorMsg: '',
    validations: [required, urlValidation]
  });

  const handleToggleOption = (options: TOption[], prevSelections: TOption[], key: string, isEnabled: boolean) => {
    if (isEnabled === true) {
      const option = options.find(opt => opt.value === key);
      const existing = prevSelections.some(opt => opt.value === key);

      if (option && !existing) {
        return [...prevSelections, option];
      }
    } else if (isEnabled === false) {
      return prevSelections.filter(opt => opt.value !== key);
    }
    return prevSelections;
  };

  useEffect(() => {
    if (isFetched) {

      Object.entries(toggles).forEach(opt => {
        const [key, value] = opt;
        const isToggle = TOGGLES_KEYS.some(k => k === key);
        const setSelected = isToggle ? setSelectedToggles : setCheckedOptions;

        if (value === true) {
          const matchingOption = (
            isToggle ?
              additionalToggleOptions
              :
              additionalCheckboxesOptions
          ).find(option => option.value === key);

          if (matchingOption) {
            setSelected(prev => {
              const existing = prev.some(opt => opt.value === matchingOption.value);
              return existing ?
                prev
                :
                [...prev, matchingOption]
            });
          }
        } else {
          setSelected(prev => prev.filter(opt => opt.value !== key));
        }
      });

      setMaxCardsSelected(max_allowed_cards || MAX_ALLOWED_CARDS_OPTIONS[0]);
      setIsInputVisible(showTerms);
      validateInputField(termsUrl);
      setSelectedToggles(prev => {
        let prevSelections = [...prev];

        prevSelections = handleToggleOption(additionalToggleOptions, prevSelections, SHOW_TERMS, showTerms);
        prevSelections = handleToggleOption(splitPaymentOptions, prevSelections, SPLIT_PAYMENT_ENABLED, split_payment_enabled);

        return prevSelections;
      });

      setIsInitialized(true);

    }
  }, [isFetched, isDataReset]);

  useEffect(() => {
    if (isInitialized) {
      const updatedCheckboxes = {
        ...toggles,
        ...Object.fromEntries(CHECKBOXES_KEYS.map(key => [key, false]))
      } as any;

      checkedOptions.forEach(option => {
        updatedCheckboxes[option.value] = true;
      });

      setCustomizationData(prevData => ({
        ...prevData,
        payments: {
          ...prevData.payments,
          toggles: updatedCheckboxes
        }
      }));
    }
  }, [checkedOptions]);

  useEffect(() => {
    if (isInitialized) {
      const updatedToggles = {
        ...toggles,
        ...Object.fromEntries(TOGGLES_KEYS.map(key => [key, false]))
      } as any;

      const updatedSplitPayment = { ...split_payment };

      selectedToggles.forEach(option => {
        updatedToggles[option.value] = true;

        updatedSplitPayment[SPLIT_PAYMENT_ENABLED] = option.value === SPLIT_PAYMENT_ENABLED;
      });

      setCustomizationData(prevData => ({
        ...prevData,
        payments: {
          ...prevData.payments,
          toggles: updatedToggles,
          split_payment: updatedSplitPayment
        }
      }));
    }

  }, [selectedToggles]);

  useEffect(() => {
    if (isInitialized) {
      setCustomizationData(prevData => ({
        ...prevData,
        payments: {
          ...prevData.payments,
          showTerms: isInputVisible
        }
      }));
    }
  }, [isInputVisible]);

  useEffect(() => {
    if (isInitialized) {
      setCustomizationData(prevData => ({
        ...prevData,
        payments: {
          ...prevData.payments,
          termsUrl: inputValue.value
        }
      }));

      isInputVisible && validateInputField(inputValue.value);
    }

  }, [isInputVisible, inputValue.value]);

  useEffect(() => {
    setErrors(TERMS_URL, isInputVisible ? inputValue.errorMsg : '');
  }, [isInputVisible, inputValue.errorMsg]);


  const handleTogglesChange = (type: 'checkbox' | 'toggle', checked: boolean, option: TOption) => {
    const isToggle = type === 'toggle';

    const setSelected = isToggle ? setSelectedToggles : setCheckedOptions;
    option.value === SHOW_TERMS && setIsInputVisible(checked);

    if (checked) {
      setSelected(prev => {
        if (prev.some(opt => opt.value === option.value)) {
          return prev;
        }
        return [...prev, option];
      });
    } else {
      setSelected(prev =>
        prev.filter(opt => opt.value !== option.value)
      );
    }
  };

  const validateInputField = (value: string) => {
    for (const validation of inputValue.validations) {
      if (!validation.pattern.test(value)) {
        setInputValue(prevState => ({
          ...prevState,
          value: value,
          isValid: false,
          errorMsg: validation.error_msg
        }));
        return
      }
      setInputValue(prevState => ({
        ...prevState,
        value: value,
        isValid: true,
        errorMsg: ''
      }));
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(prevState => ({
      ...prevState,
      value: value,
    }));
  };

  const handleMaxCardsSelection = (option: number) => {
    setMaxCardsSelected(option);

    setCustomizationData(prevData => ({
      ...prevData,
      payments: {
        ...prevData.payments,
        split_payment: {
          ...split_payment,
          max_allowed_cards: option
        }
      }
    }))
  };

  const renderText = (text: string, hint?: string): any => {
    return hint ?
      <div className={styles["additional-info__additional-hint"]}>
        <span>{text}</span>

        <PopperTooltip
          text={hint}
          placement={lang === 'en' ? "right" : "left"}
        >
          {CircleInfo}
        </PopperTooltip>
      </div>
      :
      text
  };

  return (
    <>
      <div className={styles["additional-info"]}>
        <Title
          label={t('innerLayout.checkoutCustomizationPage.tabs.payment.sections.additionalInfo.label')}
          hint={t('innerLayout.checkoutCustomizationPage.tabs.payment.sections.additionalInfo.hint')}
        />
        <div className={styles["additional-info__checkboxes-wrapper"]}>
          {
            additionalCheckboxesOptions.map((option, index) =>
              (!option.flag || merchantFlags.includes(option.flag || "")) &&
              <Checkbox
                key={`${option.value}-${index}`}
                id={option.value}
                type="checkbox"
                text={renderText(option.label, option.hint)}
                checked={checkedOptions.some(opt => opt.value === option.value)}
                onClickHandler={(checked) => handleTogglesChange('checkbox', checked, option)}
              />
            )
          }
        </div>
      </div>
      <div className={styles["additional-info__toggles-wrapper"]}>
        <Title
          label={t('innerLayout.checkoutCustomizationPage.tabs.payment.sections.splitPayment.label')}
        />

        {
          splitPaymentOptions.map((option) =>
            (!option.flag || merchantFlags.includes(option.flag || "")) &&
            <ToggleBox
              key={option.value}
              type="transparent box"
              text={renderText(option.label, option.hint)}
              checked={selectedToggles.some(opt => opt.value === option.value)}
              onToggleChangeHandler={(checked) => handleTogglesChange('toggle', checked, option)}
            />
          )
        }
        <Dropdown
          label={t('innerLayout.checkoutCustomizationPage.tabs.payment.sections.splitPayment.maxAllowedCards')}
          closeOnClickOutside
        >
          <DropdownButton
            type="input"
            text={maxCardsSelected.toString()}
          />

          <DropdownMenu
            verticalPlacement="auto"
          >
            {
              MAX_ALLOWED_CARDS_OPTIONS.map((option, index) =>
                <DropdownMenuItem
                  key={`max-allowed-cards-menu-item-${index}`}
                  text={option.toString()}
                  onClickHandler={() => handleMaxCardsSelection(option)}
                  selected={maxCardsSelected === option}
                />
              )
            }
          </DropdownMenu>
        </Dropdown>
      </div>

      <div className={styles["additional-info__toggles-wrapper"]}>
        <Title
          label={t('innerLayout.checkoutCustomizationPage.tabs.payment.sections.paymentExperience.label')}
        />

        {
          additionalToggleOptions.map((option) =>
            <Fragment key={option.value}>
              <ToggleBox
                type="transparent box"
                text={renderText(option.label, option.hint)}
                checked={selectedToggles.some(opt => opt.value === option.value)}
                onToggleChangeHandler={(checked) => handleTogglesChange('toggle', checked, option)}
              />

              {
                option.value === SHOW_TERMS && isInputVisible &&
                <Input
                  name={`${option.value}-input`}
                  placeholder={t('innerLayout.checkoutCustomizationPage.tabs.payment.sections.additionalInfo.placeholders.enterT&Cs')}
                  value={inputValue.value}
                  onChange={handleInputChange}
                  status={inputValue.isValid === false ? "error" : "default"}
                  error={inputValue.errorMsg}
                />
              }
            </Fragment>
          )
        }
      </div>
    </>
  )
}
